import React from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from '@shared';
import type { DataTableColumn } from '@shared';
import EventSeverityBadge from '@/components/EventSeverityBadge';
import { openEventDetail } from '@/components/EventDetailModal';
import Paginator from '@/components/Paginator';
import { sourceLabel } from '@/lib/eventTypeLabels';
import { linkAccentClass } from '@/lib/uiClasses';
import type { MonitorEventRow } from '@/services/api';

const muted = 'text-text-sub dark:text-slate-400';

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'medium' }) : '—';
}

function TypeCell({ row }: { row: MonitorEventRow }) {
  return (
    <div className="min-w-0">
      <p className="text-sm text-text-main dark:text-white">{row.label}</p>
      {row.label !== row.event_type ? (
        <p className={`text-[11px] font-mono ${muted}`}>{row.event_type}</p>
      ) : null}
    </div>
  );
}

function WorkshopLink({ row }: { row: MonitorEventRow }) {
  if (row.workshop_id == null) return <span className={muted}>—</span>;
  return (
    <Link
      to={`/talleres/${row.workshop_id}`}
      className={linkAccentClass}
      // La fila entera abre el detalle; el enlace sólo navega.
      onClick={(e) => e.stopPropagation()}
    >
      {row.workshop_name || row.workshop_slug || `#${row.workshop_id}`}
    </Link>
  );
}

interface Props {
  rows: MonitorEventRow[];
  total: number;
  offset: number;
  pageSize: number;
  onOffsetChange: (offset: number) => void;
  loading: boolean;
  toolbar?: React.ReactNode;
  /** Columna «Taller»: fuera cuando la tabla ya es la de un taller. */
  showWorkshop?: boolean;
  emptyLabel?: string;
  className?: string;
}

/**
 * La tabla del Registro: la usan Monitor → Registro, la ficha del taller y la
 * del partner. Paginada en servidor (el padre pide cada página con
 * `useMonitorEvents`) y sin orden en el navegador: ordenar una sola página
 * mentiría sobre el resto; el servidor ya las da de la más nueva a la más vieja.
 * Clic en una fila → detalle del evento.
 */
export default function EventLogTable({
  rows,
  total,
  offset,
  pageSize,
  onOffsetChange,
  loading,
  toolbar,
  showWorkshop = true,
  emptyLabel = 'Sin eventos',
  className,
}: Props) {
  const columns: DataTableColumn<MonitorEventRow>[] = [
    { key: 'date', header: 'Fecha', className: `text-xs whitespace-nowrap ${muted}`, render: (r) => fmtDate(r.created_at) },
    { key: 'type', header: 'Tipo', render: (r) => <TypeCell row={r} /> },
    { key: 'category', header: 'Categoría', className: 'text-xs whitespace-nowrap', render: (r) => r.category_label || '—' },
    { key: 'severity', header: 'Severidad', render: (r) => <EventSeverityBadge severity={r.severity} /> },
    { key: 'source', header: 'Origen', className: 'text-xs whitespace-nowrap', render: (r) => sourceLabel(r.source) || '—' },
    {
      key: 'description',
      header: 'Descripción',
      className: `text-xs max-w-[360px] truncate ${muted}`,
      render: (r) => (r.description ? <span title={r.description}>{r.description}</span> : '—'),
    },
    ...(showWorkshop
      ? [{ key: 'workshop', header: 'Taller', className: 'text-xs', render: (r: MonitorEventRow) => <WorkshopLink row={r} /> }]
      : []),
    {
      key: 'actor',
      header: 'Autor',
      className: `text-xs max-w-[180px] truncate ${muted}`,
      render: (r) => (r.performed_by ? <span title={r.performed_by}>{r.performed_by}</span> : '—'),
    },
  ];

  const renderMobile = (r: MonitorEventRow) => (
    <div className="space-y-1 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-text-main dark:text-white break-words min-w-0">{r.label}</p>
        <EventSeverityBadge severity={r.severity} />
      </div>
      {r.description ? <p className={`text-xs break-words line-clamp-2 ${muted}`}>{r.description}</p> : null}
      <p className={`text-[11px] ${muted}`}>
        {[fmtDate(r.created_at), r.category_label, sourceLabel(r.source), showWorkshop ? (r.workshop_name || r.workshop_slug) : null, r.performed_by]
          .filter(Boolean)
          .join(' · ')}
      </p>
    </div>
  );

  return (
    <>
      <DataTable<MonitorEventRow>
        data={rows}
        columns={columns}
        rowKey={(r) => r.id}
        onRowClick={(r) => openEventDetail(r.id)}
        renderMobile={renderMobile}
        toolbar={toolbar}
        emptyIcon="history"
        emptyLabel={loading ? 'Cargando…' : emptyLabel}
        className={className}
      />
      <Paginator offset={offset} limit={pageSize} total={total} onChange={onOffsetChange} disabled={loading} />
    </>
  );
}
